/**
 * CryptoPaymentModal — XEA's "Pay with Crypto" flow.
 *
 * Flow:
 *   1. User picks plan + clicks "Pay with Crypto" on the subscription page
 *   2. We POST /api/payments/crypto/intents → returns deposit address + EXACT
 *      amount (uniquely encoded in 3 decimals so we can match the on-chain tx)
 *   3. Modal renders the address, the QR code, the amount, a countdown timer,
 *      and polls /intents/{id} every 5s for status changes
 *   4. Once backend Celery beat detects the chain transfer + activates the
 *      subscription, status flips to "paid" → we close and refresh
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { trackFunnel } from "../lib/funnel";
import { Loader2, Copy, Check, X, ExternalLink, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAIN_LABELS = {
  polygon:  "Polygon",
  bsc:      "BNB Smart Chain (BSC)",
  tron:     "Tron (TRC20)",
  ethereum: "Ethereum",
};

const EXPLORER = {
  polygon:  (hash) => `https://polygonscan.com/tx/${hash}`,
  bsc:      (hash) => `https://bscscan.com/tx/${hash}`,
  tron:     (hash) => `https://tronscan.org/#/transaction/${hash}`,
  ethereum: (hash) => `https://etherscan.io/tx/${hash}`,
};

export const CryptoPaymentModal = ({ open, plan, token, onClose, onSuccess }) => {
  const authToken = localStorage.getItem("token");
  const [chain, setChain]   = useState("polygon");
  const [stable, setStable] = useState("USDT");
  const [availability, setAvailability] = useState(null); // { polygon: {USDT:bool,USDC:bool}, ... }
  const [globallyEnabled, setGloballyEnabled] = useState(true);
  const [disabledReason, setDisabledReason] = useState(null);
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [copied, setCopied] = useState({ amount: false, address: false });

  // ─── Availability fetch (once per open) ─────────────────────────────────
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/crypto/availability`);
        setAvailability(r.data?.combos || null);
        setGloballyEnabled(r.data?.enabled !== false);
        setDisabledReason(r.data?.disabled_reason || null);
      } catch (_) { /* non-fatal */ }
    })();
  }, [open]);

  // Auto-snap to a supported combo if the current one is disabled
  useEffect(() => {
    if (!availability) return;
    if (availability?.[chain]?.[stable] === false) {
      // find first chain that supports the current token
      const newChain = Object.keys(availability).find(c => availability[c][stable]);
      if (newChain) {
        setChain(newChain);
      } else {
        // otherwise switch token to one that's supported on current chain
        const newToken = Object.keys(availability[chain] || {}).find(t => availability[chain][t]);
        if (newToken) setStable(newToken);
      }
    }
  }, [availability, chain, stable]);

  // ─── Create the payment intent ──────────────────────────────────────────
  const createIntent = async () => {
    if (!plan) return;
    setLoading(true);
    setIntent(null);
    try {
      const r = await axios.post(
        `${API}/payments/crypto/intents`,
        { plan, chain, token: stable },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setIntent(r.data);
      const exp = new Date(r.data.expires_at).getTime();
      setRemaining(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
      // Funnel — stage 3: checkout_started (intent created successfully)
      trackFunnel("checkout_started", { tier: plan, method: "crypto" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't start crypto payment";
      toast.error(typeof msg === "string" ? msg : "Couldn't start crypto payment");
      // Close out so user isn't stuck on a blank modal
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  // Re-create the intent whenever chain or token changes (or modal opens)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open && plan) createIntent();
  }, [open, plan, chain, stable]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Countdown + status poll ────────────────────────────────────────────
  useEffect(() => {
    if (!intent || intent.status !== "pending") return;
    const tickIv = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    const pollIv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/payments/crypto/intents/${intent.id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setIntent(r.data);
        if (r.data.status === "paid") {
          toast.success(`Payment confirmed — ${plan.charAt(0).toUpperCase() + plan.slice(1)} activated!`);
          // Funnel — stage 4: payment_completed
          trackFunnel("payment_completed", { tier: plan, method: "crypto" });
          clearInterval(tickIv);
          clearInterval(pollIv);
          setTimeout(() => {
            onSuccess?.();
            onClose?.();
          }, 1500);
        } else if (r.data.status === "expired") {
          clearInterval(tickIv);
          clearInterval(pollIv);
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => { clearInterval(tickIv); clearInterval(pollIv); };
  }, [intent, plan, authToken, onClose, onSuccess]);

  // ─── Helpers ────────────────────────────────────────────────────────────
  const copy = async (kind, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [kind]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [kind]: false })), 1500);
    } catch { toast.error("Couldn't copy"); }
  };
  const mins = String(Math.floor(remaining / 60)).padStart(2, "0");
  const secs = String(remaining % 60).padStart(2, "0");

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="crypto-payment-modal"
        className="max-w-lg p-0 overflow-hidden border-ink-200/70"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-ink-950 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-citrus-500">
              Pay with Crypto · {plan?.toUpperCase()}
            </p>
            <p className="text-sm font-display font-bold mt-0.5">
              Funds settle directly to our OKX wallet
            </p>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
            data-testid="crypto-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* When the master switch is OFF, replace the whole flow with a
            friendly "Coming back soon" panel. We still render the modal
            chrome so users see we acknowledged the click. */}
        {!globallyEnabled ? (
          <div
            className="px-6 py-12 flex flex-col items-center text-center"
            data-testid="crypto-disabled-panel"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-100 text-amber-600 mb-4">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <p className="text-base font-display font-bold text-ink-950 tracking-tight">
              Crypto payments — back soon
            </p>
            <p className="text-sm text-ink-500 mt-2 max-w-sm leading-relaxed">
              {disabledReason || "We're finishing setup on our deposit addresses. Crypto will be back online shortly — please use PayPal in the meantime."}
            </p>
            <Button
              onClick={() => onClose?.()}
              data-testid="crypto-disabled-close-btn"
              className="mt-6 h-11 px-6 bg-ink-950 hover:bg-coral-500 text-white text-xs font-bold uppercase tracking-[0.18em]"
            >
              Got it
            </Button>
          </div>
        ) : (
        <>
        {/* Chain + token chooser — combos not configured on the backend are
            hidden entirely (no line-through buttons) for a cleaner look. */}
        <div className="px-6 pt-5 pb-2 grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500 mb-1.5">Network</p>
            <div className="grid grid-cols-4 gap-1 p-1 rounded-xl bg-ink-100">
              {["polygon", "bsc", "tron", "ethereum"].map((c) => {
                const supported = availability ? !!availability?.[c]?.[stable] : true;
                if (!supported) return null;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setChain(c)}
                    data-testid={`crypto-chain-${c}`}
                    className={`text-[10px] font-bold uppercase tracking-[0.14em] py-1.5 rounded-lg transition-colors ${
                      chain === c
                        ? "bg-ink-950 text-citrus-500"
                        : "text-ink-500 hover:text-ink-950"
                    }`}
                  >
                    {c === "bsc" ? "BSC" : c === "ethereum" ? "ETH" : c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500 mb-1.5">Token</p>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-ink-100">
              {["USDT", "USDC"].map((t) => {
                const supported = availability ? !!availability?.[chain]?.[t] : true;
                if (!supported) return null;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setStable(t)}
                    data-testid={`crypto-token-${t.toLowerCase()}`}
                    className={`text-[11px] font-bold uppercase tracking-[0.14em] py-1.5 rounded-lg transition-colors ${
                      stable === t
                        ? "bg-ink-950 text-citrus-500"
                        : "text-ink-500 hover:text-ink-950"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 pt-3 min-h-[360px]" data-testid="crypto-payment-body">
          {loading || !intent ? (
            <div className="flex flex-col items-center justify-center py-16 text-ink-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-xs uppercase tracking-[0.18em] font-bold mt-3">
                Generating deposit address…
              </p>
            </div>
          ) : intent.status === "paid" ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 mb-4">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="font-display font-black text-2xl tracking-[-0.03em] text-ink-950">
                Payment confirmed!
              </h3>
              <p className="text-sm text-ink-500 mt-2 max-w-sm">
                Your {plan} subscription is active for 30 days.
                {intent.tx_hash && EXPLORER[intent.chain] && (
                  <a
                    href={EXPLORER[intent.chain](intent.tx_hash)}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-2 inline-flex items-center gap-1 text-coral-500 hover:text-coral-600 font-medium text-xs"
                    data-testid="crypto-tx-explorer-link"
                  >
                    View on explorer <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </p>
            </div>
          ) : intent.status === "expired" ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-lg font-display font-bold text-ink-950">This payment expired.</p>
              <p className="text-sm text-ink-500 mt-2">Click below to generate a fresh one.</p>
              <Button onClick={createIntent} className="mt-4" data-testid="crypto-retry-btn">
                Try again
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Ethereum gas warning */}
              {intent.chain === "ethereum" && (
                <div
                  className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2.5"
                  data-testid="crypto-eth-gas-warning"
                >
                  <span className="text-amber-600 mt-0.5">⚠️</span>
                  <p className="text-[11px] leading-relaxed text-amber-900 font-medium">
                    <strong>Heads up:</strong> Ethereum gas fees are typically $3&ndash;$15 per transaction.
                    Polygon, BSC and Tron settle in seconds and cost &lt;$0.10.
                  </p>
                </div>
              )}

              {/* QR + address card */}
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-ink-50 border border-ink-200/70">
                <div className="bg-white p-2 rounded-xl border border-ink-200/70 shrink-0">
                  <QRCodeSVG value={intent.address} size={120} level="M" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                    Send to ({CHAIN_LABELS[intent.chain]})
                  </p>
                  <p
                    className="font-mono text-xs break-all text-ink-950 mt-1 select-all"
                    data-testid="crypto-deposit-address"
                  >
                    {intent.address}
                  </p>
                  <button
                    type="button"
                    onClick={() => copy("address", intent.address)}
                    data-testid="crypto-copy-address"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-coral-500 hover:text-coral-600"
                  >
                    {copied.address ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied.address ? "Copied" : "Copy address"}
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 rounded-2xl bg-citrus-500/10 border border-citrus-500/30">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                      Subscription price
                    </p>
                    <p
                      className="font-display font-black text-3xl tracking-[-0.03em] text-ink-950 mt-0.5"
                      data-testid="crypto-base-price"
                    >
                      ${(intent.base_price_usd ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500 px-2.5 py-1 rounded-full bg-coral-500/10">
                    {intent.token}
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-citrus-500/30">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                    Transfer <span className="text-coral-500">exactly</span> this amount
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p
                      className="font-mono font-bold text-xl tracking-tight text-ink-950 select-all"
                      data-testid="crypto-expected-amount"
                    >
                      {intent.expected_amount.toFixed(2)}
                    </p>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-700">
                      {intent.token}
                    </p>
                  </div>
                  <p className="text-[10px] text-ink-500 mt-1.5 leading-relaxed">
                    1 {intent.token} &asymp; $1 USD &mdash; send the exact amount on the {CHAIN_LABELS[intent.chain]} network.
                  </p>
                  <button
                    type="button"
                    onClick={() => copy("amount", intent.expected_amount.toFixed(2))}
                    data-testid="crypto-copy-amount"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-coral-500 hover:text-coral-600"
                  >
                    {copied.amount ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied.amount ? "Copied" : "Copy amount"}
                  </button>
                </div>
              </div>

              {/* Status + timer */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-ink-200/70">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-coral-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex w-2 h-2 rounded-full bg-coral-500" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-950">
                    Waiting for transfer
                  </span>
                </div>
                <span
                  className="text-xs font-mono font-bold text-ink-700"
                  data-testid="crypto-countdown"
                >
                  Expires in {mins}:{secs}
                </span>
              </div>

              <p className="text-[10px] text-ink-500 leading-relaxed text-center">
                Confirmation usually takes 1&ndash;2 minutes after the transfer lands on-chain.
                Don&apos;t close this window &mdash; we&apos;ll activate your subscription automatically.
              </p>
            </div>
          )}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CryptoPaymentModal;
